
import { CardapioWebConfig } from '../types';
import { getFunctionsBaseUrl, supabase } from './supabase';

export const getRobustSaaSId = (storeName: string = 'loja', storeId: string = 'default') => {
    const cleanName = storeName.toLowerCase().trim().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
    const prefix = storeId.substring(0, 6).toUpperCase();
    return `${cleanName}-${prefix}`;
};

const getProxiedUrl = (url: string): string => {
    const proxyBase = `${getFunctionsBaseUrl()}/sal-cardapio-proxy`;
    return `${proxyBase}?url=${encodeURIComponent(url.trim())}`;
};

const getAuthHeaders = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    const headers: Record<string, string> = {};
    if (session?.access_token) headers['Authorization'] = `Bearer ${session.access_token}`;
    return headers;
};

const fetchWithRetry = async (url: string, options: RequestInit, retries = 1, backoff = 1000): Promise<Response> => {
    // FIX: Evita double-encoding decodificando antes de re-codificar para o proxy
    const cleanUrl = decodeURIComponent(url);
    const proxiedUrl = getProxiedUrl(cleanUrl);

    const headers = new Headers(options.headers || {});
    const authHeaders = await getAuthHeaders();
    Object.entries(authHeaders).forEach(([key, value]) => {
        headers.set(key, value);
    });

    const newOptions = { ...options, headers };

    try {
        const response = await fetch(proxiedUrl, newOptions);
        return response;
    } catch (error) {
        if (retries > 0) {
            await new Promise(res => setTimeout(res, backoff));
            return fetchWithRetry(url, options, retries - 1, backoff * 2);
        }
        throw error;
    }
};

const getHeaders = (config: CardapioWebConfig) => {
    const headers: Record<string, string> = { 'Content-Type': 'application/json', 'Accept': 'application/json' };
    if (config.apiToken) headers['X-API-KEY'] = config.apiToken;
    return headers;
};

export const fetchFromWebhookBuffer = async (storeId: string): Promise<any[]> => {
    if (!storeId) return [];
    try {
        const { data, error } = await supabase
            .from('sal_cardapioweb_buffer')
            .select('payload')
            .eq('store_id', storeId)
            .eq('processed', false);
        if (error || !data) return [];
        return data.map((row: any) => row.payload);
    } catch (e) { }
    return [];
};

export const pollCardapioOrders = async (config: CardapioWebConfig, lastPollTime?: number, strict = false): Promise<any[]> => {
    if (!config.active || !config.proxyBaseUrl) return [];
    const baseUrl = config.proxyBaseUrl.trim().replace(/\/+$/, '');

    let url = `${baseUrl}/api/partner/v1/orders`;
    const params = new URLSearchParams();

    params.append('_cb', Date.now().toString());

    if (lastPollTime) {
        const safeMinTime = Date.now() - (23 * 60 * 60 * 1000);
        // v44.4: Janela de sobreposição reduzida para 5 minutos.
        const targetTime = lastPollTime - (5 * 60 * 1000);
        const effectiveTime = Math.max(targetTime, safeMinTime);
        const sinceDate = new Date(effectiveTime).toISOString();
        params.append('updated_since', sinceDate);
    }

    const finalUrl = `${url}?${params.toString()}`;

    try {
        let response = await fetchWithRetry(finalUrl, { method: 'GET', headers: getHeaders(config) });

        if (!response.ok && response.status === 400 && params.has('updated_since')) {
            params.delete('updated_since');
            const fallbackUrl = `${url}?${params.toString()}`;
            response = await fetchWithRetry(fallbackUrl, { method: 'GET', headers: getHeaders(config) });
        }

        if (!response.ok) {
            if (strict) throw new Error(`CardapioWeb polling failed: HTTP ${response.status}`);
            return [];
        }

        const data = await response.json();
        if (Array.isArray(data)) return data;
        if (data && Array.isArray(data.orders)) return data.orders;
        return [];
    } catch (e) {
        if (strict) throw e;
        return [];
    }
};

export const getCardapioOrderDetails = async (config: CardapioWebConfig, orderId: string): Promise<any> => {
    if (!config.active || !config.proxyBaseUrl) return null;
    const baseUrl = config.proxyBaseUrl.trim().replace(/\/+$/, '');
    const url = `${baseUrl}/api/partner/v1/orders/${orderId}?_cb=${Date.now()}`;
    try {
        const response = await fetchWithRetry(url, { method: 'GET', headers: getHeaders(config) });
        if (response.ok) return await response.json();
    } catch (e) { }
    return null;
};

export const confirmCardapioOrder = (config: CardapioWebConfig, orderId: string) => sendCardapioSignal(config, orderId, 'confirm');
export const startPreparationCardapioOrder = (config: CardapioWebConfig, orderId: string) => sendCardapioSignal(config, orderId, 'start_preparation');
export const readyCardapioOrder = (config: CardapioWebConfig, orderId: string) => sendCardapioSignal(config, orderId, 'ready');
export const dispatchCardapioOrder = (config: CardapioWebConfig, orderId: string) => sendCardapioSignal(config, orderId, 'ready');
export const deliveredCardapioOrder = (config: CardapioWebConfig, orderId: string) => sendCardapioSignal(config, orderId, 'delivered');
export const finalizeCardapioOrder = (config: CardapioWebConfig, orderId: string) => sendCardapioSignal(config, orderId, 'finalize');
export const cancelCardapioOrder = (config: CardapioWebConfig, orderId: string, reason: string) => sendCardapioSignal(config, orderId, 'cancel', { cancellation_reason: reason });

const sendCardapioSignal = async (config: CardapioWebConfig, orderId: string, endpoint: string, body: any = {}): Promise<boolean> => {
    if (!config.active || !config.proxyBaseUrl) return false;
    const baseUrl = config.proxyBaseUrl.trim().replace(/\/+$/, '');
    const url = `${baseUrl}/api/partner/v1/orders/${orderId}${endpoint ? `/${endpoint}` : ''}`;
    try {
        const requestOptions: RequestInit = { method: 'POST', headers: getHeaders(config) };
        if (body && Object.keys(body).length > 0) {
            requestOptions.body = JSON.stringify(body);
        }
        const response = await fetchWithRetry(url, requestOptions);
        return response.status === 204 || response.ok;
    } catch (e) { return false; }
};
